import { slugify } from '@schuelerhilfe/shared/src/utils/slugify';
import type { BlockDto } from '@/lib/api';

/** The article body's section unit (spec 10.03.03.25). */
const COLLAPSIBLE_BLOCK_TYPE = 'layouts.collapsible-block';

/**
 * The Author Box's fixed anchor, reserved here so a body section titled "Autor"
 * gets `autor-2` and the article chip's `#autor` link can only ever reach the
 * box. Owned by this module because reserving it is the only thing that gives
 * it meaning.
 */
export const AUTHOR_ANCHOR = 'autor';

export interface TableOfContentsEntry {
  /** The Collapsible Block's Strapi component id, or its body position. */
  blockId: string;
  title: string;
  anchor: string;
}

/** Shared by both passes, so a block with no id still matches its entry. */
const blockKey = (block: unknown, index: number): string => {
  const id = (block as { id?: unknown } | null)?.id;
  return id == null ? `index:${index}` : String(id);
};

/**
 * The article's `Inhalt` list, derived from its body.
 *
 * **This one pass is the single source of anchors.** Its output drives both
 * the contents links and the `anchorId` written onto each Article Section —
 * an `id` and an `href` computed independently would diverge the moment two
 * sections share a title.
 *
 * On a live render the backend does this walk and delivers both halves on the
 * article DTO (`packages/backend/src/magazine/articleTableOfContents.ts`),
 * because the `Inhalt` list and the body are separate blocks on the Template
 * Page. This module stays the rule set for the Strapi preview, where the
 * article exists only as a client-side draft snapshot the backend never sees,
 * and both implementations are pinned to the same fixtures.
 *
 * Only the top level is walked: a Collapsible Block wrapped in a Gradient or
 * Card Section renders and expands normally but produces no entry, so the
 * contents stay a map of the article's own structure rather than of every
 * collapsible anywhere inside it. Headings inside a rich-text block are not
 * entries either.
 */
export function extractTableOfContents(
  blocks: readonly BlockDto[] | undefined,
): TableOfContentsEntry[] {
  const entries: TableOfContentsEntry[] = [];
  // `autor` belongs to the Author Box, so a section slugifying to it starts
  // at `autor-2` and the chip's `#autor` link can only reach the box.
  const taken = new Set<string>([AUTHOR_ANCHOR]);

  (blocks ?? []).forEach((block, index) => {
    if (block.componentType !== COLLAPSIBLE_BLOCK_TYPE) {
      return;
    }
    const title = typeof block.title === 'string' ? block.title : '';
    if (!title) {
      return;
    }

    // The title is the only source: there is no editor-facing anchor field
    // (10.03.03.25 § AC 4), so renaming a section always moves its anchor.
    const anchor = slugify(title);

    // A title that slugifies to nothing — "!!!", or punctuation only — gets no
    // contents entry at all rather than a made-up anchor. There is nothing to
    // label the link with, and a section missing from the list reads better
    // than an entry whose text is punctuation.
    if (!anchor) {
      return;
    }

    entries.push({
      blockId: blockKey(block, index),
      title,
      anchor: reserve(anchor, taken),
    });
  });

  return entries;
}

/** First free of `base`, `base-2`, `base-3`, … — in body order. */
function reserve(base: string, taken: Set<string>): string {
  let anchor = base;
  let suffix = 1;
  while (taken.has(anchor)) {
    suffix += 1;
    anchor = `${base}-${suffix}`;
  }
  taken.add(anchor);
  return anchor;
}

/**
 * Writes each entry's anchor onto its Collapsible Block's config, which is
 * where `CollapsibleBlockComponent` reads it from. Blocks with no entry —
 * every nested one — keep `anchorId` undefined and fall back to slugifying
 * their own title, which is correct: they have no contents entry to disagree
 * with.
 */
export function applyAnchors(
  blocks: readonly BlockDto[] | undefined,
  entries: readonly TableOfContentsEntry[],
): BlockDto[] {
  if (!blocks || blocks.length === 0) {
    return [];
  }
  const anchorByBlockId = new Map(
    entries.map((entry) => [entry.blockId, entry.anchor]),
  );

  return blocks.map((block, index) => {
    const anchor = anchorByBlockId.get(blockKey(block, index));
    return anchor ? ({ ...block, anchorId: anchor } as BlockDto) : block;
  });
}
